import FolderIcon from "@mui/icons-material/Folder";
import {
  Alert,
  AlertTitle,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Link as RouterLink } from "react-router";

import { useAuth } from "@/providers";
import { FormattedDateTime } from "@/shared/datetime";
import { useDatasetsQuery } from "@/shared/domain/datasets/hooks";
import { Principal } from "@/shared/principal";

interface RecentDatasetsProps {
  count: number;
}

export const RecentDatasets: React.FC<RecentDatasetsProps> = ({ count }) => {
  const theme = useTheme();
  const { currentOrganization } = useAuth();

  /**
   * Note: Why we're using pageSize over query.limit here
   *
   * The backend overrides `query.limit` to an uncapped sentinel for non-RoboQL
   * structured queries (see `QueryService.submit_structured_query` in the
   * roboto_service package), so a limit declared in the query body has no
   * effect server-side. This is for backward-compat reason to support old
   * search APIs. Once those have been fully deprecated we can use limit here.
   *
   * As a result, we're using pageSize but not using the pagination token
   * to achieve similar results.
   */
  const datasetsQuery = useDatasetsQuery({
    orgId: currentOrganization?.org_id,
    query: { sort_by: "created", sort_direction: "DESC" },
    pageSize: count,
  });

  const pageData = datasetsQuery.data ?? [];

  let listOrAlertContent;

  if (datasetsQuery.isLoading) {
    listOrAlertContent = (
      <Box
        sx={{
          height: "90%",
          pt: theme.spacing(1),
          pb: theme.spacing(2),
          minHeight: "250px",
        }}
      >
        <Skeleton animation="wave" variant="rounded" height={"100%"} />
      </Box>
    );
  } else if (datasetsQuery.error) {
    listOrAlertContent = (
      <Alert
        severity="error"
        sx={{
          mt: theme.spacing(1),
          mb: theme.spacing(2),
        }}
      >
        <AlertTitle>Error</AlertTitle>
        {datasetsQuery.error?.message}
      </Alert>
    );
  } else if (pageData.length === 0) {
    listOrAlertContent = (
      <Alert
        severity="warning"
        sx={{
          mt: theme.spacing(1),
          mb: theme.spacing(2),
        }}
      >
        <AlertTitle>Datasets</AlertTitle>
        No datasets have been created yet. Use the Create Dataset button above
        to create one and upload data.
      </Alert>
    );
  } else {
    listOrAlertContent = (
      <List>
        {pageData.map((dataset, idx) => {
          return (
            <ListItem
              key={idx}
              sx={{
                pl: 0,
                pr: 0,
                pt: theme.spacing(0.25),
              }}
            >
              <ListItemButton
                sx={{
                  pt: 0,
                  pb: 0,
                }}
                component={RouterLink}
                to={`/datasets/${dataset.dataset_id}`}
                disableRipple
                disableTouchRipple
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                {dataset.dataset_id !== undefined && (
                  <ListItemText
                    primary={
                      dataset.dataset_id +
                      (dataset.name ? ` — ${dataset.name}` : "")
                    }
                    primaryTypographyProps={{
                      color: theme.palette.primary.main,
                      variant: "body2",
                      component: "span",
                    }}
                    secondaryTypographyProps={{
                      component: "span",
                      variant: "caption",
                    }}
                    secondary={
                      <>
                        {dataset.created && (
                          <FormattedDateTime value={dataset.created} />
                        )}{" "}
                        by{" "}
                        {Principal.fromString(dataset.created_by).ok
                          ?.unqualifiedId ?? dataset.created_by}
                      </>
                    }
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  }

  return <div>{listOrAlertContent}</div>;
};
